/**
 * チームの担当の取得と設定。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business チームの担当を取得・設定・削除するフック
 * @graph-connects /team-assignments [calls] チームの担当 API
 * @graph-connects teamAssignmentsApi [calls] チームの担当 API クライアント
 * @graph-connects queryKeys [calls] TanStack Query キー生成
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { teamAssignmentsApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { TeamAssignmentInput } from '../lib/api/team-assignments.js';

/**
 * 担当の一覧を取る（突き合わせの結果と選択肢つき）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 事業×チームの担当と、Workspace との突き合わせ結果を取得する
 * @graph-connects teamAssignmentsApi.list [calls] 担当の一覧取得
 */
export function useTeamAssignments() {
  return useQuery({
    queryKey: queryKeys.teamAssignments.list(),
    queryFn: () => teamAssignmentsApi.list(),
  });
}

/**
 * 一覧を読み直す。
 *
 * 設定も削除も、担当の行だけでなく「担当が無いチーム」の一覧も動かすので、
 * **返りを部分的に差し替えずに引き直す**（片方だけ更新すると突き合わせがずれる）。
 * @graph-connects queryKeys [calls] TanStack Query キー生成
 */
function useRefreshTeamAssignments(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.teamAssignments.all });
  };
}

/**
 * 担当を設定する（同じ 事業 × チームは上書き）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 事業×チームの担当を登録・更新する
 * @graph-connects teamAssignmentsApi.upsert [calls] 担当の登録・更新
 * @graph-connects useRefreshTeamAssignments [calls] 設定後の一覧再取得
 */
export function useUpsertTeamAssignment() {
  const refresh = useRefreshTeamAssignments();
  return useMutation({
    mutationFn: (input: TeamAssignmentInput) => teamAssignmentsApi.upsert(input),
    onSuccess: refresh,
  });
}

/**
 * 担当を消す（チームが無くなったとき）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 事業×チームの担当を削除する
 * @graph-connects teamAssignmentsApi.remove [calls] 担当の削除
 * @graph-connects useRefreshTeamAssignments [calls] 削除後の一覧再取得
 */
export function useDeleteTeamAssignment() {
  const refresh = useRefreshTeamAssignments();
  return useMutation({
    mutationFn: ({ business, team }: { business: string; team: string }) =>
      teamAssignmentsApi.remove(business, team),
    onSuccess: refresh,
  });
}
